import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useCreateProject } from "../../hooks/useProjects";

interface Props {
  onClose: () => void;
}

const NAME_MAX = 255;

export function CreateProjectModal({ onClose }: Props) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [validationError, setValidationError] = useState<string | null>(null);
  const navigate = useNavigate();
  const mutation = useCreateProject();

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);
    const trimmed = name.trim();
    if (trimmed.length < 1) {
      setValidationError("Project name is required.");
      return;
    }
    if (trimmed.length > NAME_MAX) {
      setValidationError(`Project name must be ≤ ${NAME_MAX} characters.`);
      return;
    }

    try {
      const project = await mutation.mutateAsync({
        name: trimmed,
        description: description.trim() || undefined,
      });
      onClose();
      navigate(`/projects/${project.id}`);
    } catch {
      // mutation.isError surfaces the failure below.
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="create-project-title"
      data-testid="create-project-modal"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 50,
      }}
    >
      <div
        style={{
          background: "white",
          padding: "1.5rem",
          borderRadius: "0.5rem",
          width: "min(28rem, 90vw)",
        }}
      >
        <h2 id="create-project-title" style={{ marginTop: 0 }}>
          New Project
        </h2>
        <form onSubmit={onSubmit}>
          <label
            htmlFor="project-name"
            style={{ display: "block", marginBottom: "0.25rem" }}
          >
            Name
          </label>
          <input
            id="project-name"
            data-testid="project-name-input"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={NAME_MAX + 1}
            required
            style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem" }}
          />

          <label
            htmlFor="project-description"
            style={{ display: "block", marginBottom: "0.25rem" }}
          >
            Description (optional)
          </label>
          <textarea
            id="project-description"
            data-testid="project-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            style={{ width: "100%", marginBottom: "1rem", padding: "0.5rem" }}
          />

          {validationError && (
            <div role="alert" style={{ color: "#b91c1c", marginBottom: "0.5rem" }}>
              {validationError}
            </div>
          )}
          {mutation.isError && (
            <div role="alert" style={{ color: "#b91c1c", marginBottom: "0.5rem" }}>
              {mutation.error instanceof Error
                ? mutation.error.message
                : "Failed to create project."}
            </div>
          )}

          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <button type="button" onClick={onClose} disabled={mutation.isPending}>
              Cancel
            </button>
            <button
              type="submit"
              data-testid="create-project-submit"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? "Creating…" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
